import React, { useEffect, useMemo, useState } from 'react'

import {
  arrayOf, bool, object, oneOfType, shape, string,
} from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import MainFilters from './Components/MainFilters/MainFilters'
import Results from './Components/Results/Results'
import SecondaryFilters from './Components/SecondaryFilters/SecondaryFilters'
import { withDevFallbackTags } from './devFallbackTags'
import { pristineFilterState, searchPageStore, seedSearchPageStore, useSearchPageStore } from './Store'
import { DEFAULT_PER_PAGE, fetchResults, throttledFetchResults } from './Store/actions'
import buildSearchParams from './Store/buildSearchParams'
import hydrateStateFromUrl from './Store/hydrateStateFromUrl'
import normalizeMeta from './Store/normalizeMeta'

// Refetch only when the mapped backend params change. buildSearchParams already isolates the
// filter-relevant fields, so this ignores results/isLoading/page writes and never loops.
// Also owns popstate: every successful fetch pushes a history entry (Store/actions/results.js),
// so back/forward must restore the state the restored URL encodes - otherwise the address bar
// changes while filters and results stay frozen.
const subscribeToFilterAndHistoryChanges = () => {
  let prevSignature = JSON.stringify(buildSearchParams(searchPageStore.getState()))

  const unsubscribeStore = searchPageStore.subscribe((state) => {
    const signature = JSON.stringify(buildSearchParams(state))
    if (signature !== prevSignature) {
      prevSignature = signature
      throttledFetchResults()
    }
  })

  const handlePopstate = () => {
    // A trailing throttled call scheduled by pre-Back filter changes would fire after the
    // restore with page 1 and URL pushes enabled, clobbering the restored entry - drop it.
    throttledFetchResults.cancel({ upcomingOnly: true })
    const { pathname, search } = window.location
    // Reset to pristine defaults first so filters absent from the restored URL are cleared.
    const nextFilters = { ...pristineFilterState, ...hydrateStateFromUrl(pathname + search) }
    // Pre-align the signature: the setState below must not trigger the subscriber's own
    // (page-less, URL-pushing) fetch on top of the explicit one that follows.
    prevSignature = JSON.stringify(buildSearchParams(nextFilters))
    searchPageStore.setState(nextFilters)

    const urlParams = new URLSearchParams(search)
    fetchResults({
      page: Number(urlParams.get('page')) || 1,
      // Explicit default: falling back to meta.perPage would reuse the per-page of the entry
      // we just navigated AWAY from, not the one the restored URL encodes.
      perPage: Number(urlParams.get('per_page')) || DEFAULT_PER_PAGE,
      skipUrlUpdate: true,
    })
  }

  window.addEventListener('popstate', handlePopstate)

  return () => {
    // `upcomingOnly` clears the pending trailing call without latching the throttle's `cancelled`
    // flag - throttledFetchResults is a module singleton, so a bare cancel() would kill refetching
    // for every later mount (including StrictMode's mount -> unmount -> mount in dev).
    throttledFetchResults.cancel({ upcomingOnly: true })
    unsubscribeStore()
    window.removeEventListener('popstate', handlePopstate)
  }
}

// Backend (Items::SerializeCollection) emits tags as `[["slug", { thumbnailUrl, ... }], ...]`.
// Flatten to a simple `string[]` here so downstream filters work with a single shape.
function SearchPage({
  data = EMPTY_ARRAY,
  meta = EMPTY_OBJECT,
  filtersOptions = EMPTY_OBJECT,
  tags = EMPTY_ARRAY,
  url = '',
  params = EMPTY_OBJECT,
  isMobileSSR = false,
  shouldShowAllItemsOnMobile = false,
}) {
  // Seed the store with SSR results synchronously on first render (server + client) so the
  // gallery is part of the prerendered markup and hydration does not shift layout. Filters from
  // the canonical URL are seeded too, so landing on a filtered link keeps those filters applied
  // (and reflected in the controls) instead of dropping them on the next fetch. The `url` prop is
  // used over window.location so server and client seed identically.
  useState(() => {
    seedSearchPageStore({
      ...hydrateStateFromUrl(url),
      results: data,
      meta: normalizeMeta(meta),
      filtersOptions,
      resultTags: tags,
    })
    return true
  })

  // Read tags from the store (updated on every fetch), not the SSR prop, so contextual
  // suggestions stay in sync with the current filter set.
  const resultTags = useSearchPageStore(state => state.resultTags)

  const suggestedTags = useMemo(() => {
    const normalized = resultTags
      .map(entry => (Array.isArray(entry) ? entry[0] : entry))
      .filter(Boolean)
    return withDevFallbackTags(normalized)
  }, [resultTags])

  useEffect(() => subscribeToFilterAndHistoryChanges(), [])

  const pageConfig = usePageConfig()

  // Actor-only filters hydrated from a shared logged-in URL (licenses[]=wish_list_only etc.) are
  // ignored by ItemsSearch without current_user, and the gated controls are hidden - leaving an
  // invisible "active" filter that every later request keeps re-sending. Once pageConfig confirms
  // a signed-out viewer, drop them; the store subscription then refetches and rewrites the URL.
  useEffect(() => {
    if (!pageConfig || pageConfig.currentUserId) return
    const { personalFilter, purchaseFilter } = searchPageStore.getState()
    if (personalFilter === undefined && purchaseFilter === undefined) return
    searchPageStore.setState({ personalFilter: undefined, purchaseFilter: undefined })
  }, [pageConfig])

  return (
    <div className="search-page">
      <div className="search-page__filters-container">
        <MainFilters />
        <SecondaryFilters suggestedTags={suggestedTags} />
      </div>
      <Results
        // The request params echoed by the backend are the only SSR-safe locale source: document
        // is absent during the fast-page SSR render, so reading <html lang> would always seed 'en'
        // and translate the server markup wrong on /de, /fr, ... (ListingPage precedent).
        locale={params.locale || 'en'}
        isMobileSSR={isMobileSSR}
        shouldShowAllItemsOnMobile={shouldShowAllItemsOnMobile}
      />
    </div>
  )
}

SearchPage.propTypes = {
  data: arrayOf(object),
  meta: object,
  filtersOptions: object,
  tags: arrayOf(oneOfType([
    arrayOf(oneOfType([string, shape({})])),
    string,
  ])),
  url: string,
  params: object,
  isMobileSSR: bool,
  shouldShowAllItemsOnMobile: bool,
}

export default SearchPage
